import { Link } from 'react-router-dom';
import { get } from '../api';
import { currentLocale } from '../dates';
import { hoursLabel } from '../duration';
import { useLoad } from '../hooks';

interface Entry {
  id: number;
  action: string;
  who: string;
  when: string;
  changes: { column: string; old: string | null; new: string | null }[];
}

/** Columns whose stored name or unit is not what the office calls them. */
const SHOWN: Record<string, { label: string; value(v: string): string }> = {
  duration_minutes: { label: 'Length', value: (v) => hoursLabel(Number(v)) },
};
const valueOf = (column: string, v: string | null) =>
  v === null || v === '' ? '—' : (SHOWN[column]?.value(v) ?? v);

/** `first_name` as "First name". */
export const labelOf = (column: string) => {
  if (SHOWN[column]) return SHOWN[column].label;
  const words = column.replace(/_id$/, '').replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const when = (iso: string) =>
  new Date(iso).toLocaleString(currentLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/**
 * What changed on one record, field by field: the old value, the new value, who
 * and when. It only has something to show for record types whose auditing is
 * switched on in Settings, so an empty list says how to get one.
 */
export function HistoryTable(props: {
  table: 'contacts' | 'assignments' | 'visits';
  id: number | string;
}) {
  const history = useLoad(
    () => get<{ items: Entry[] }>(`/api/history?table=${props.table}&id=${props.id}`),
    [props.table, props.id],
  );
  const items = Array.isArray(history.data?.items) ? history.data!.items : [];
  const rows = items.flatMap((e) =>
    e.changes.map((c) => ({ key: `${e.id}-${c.column}`, entry: e, change: c })),
  );

  return (
    <section aria-labelledby="history-h" className="stack">
      <h2 id="history-h">History</h2>
      {history.error && (
        <p role="alert" className="error">
          {history.error}
        </p>
      )}
      {history.data && rows.length === 0 && (
        <p className="muted">
          No changes recorded. Change history is switched on per record type in{' '}
          <Link to="/settings?tab=audit">Settings</Link>.
        </p>
      )}
      {rows.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Old value</th>
              <th>New value</th>
              <th>Who</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ key, entry, change }) => (
              <tr key={key}>
                <td>{labelOf(change.column)}</td>
                <td className="muted">{valueOf(change.column, change.old)}</td>
                <td>{valueOf(change.column, change.new)}</td>
                <td>{entry.who || '—'}</td>
                <td className="muted">{when(entry.when)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
